import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Checkbox } from "@/components/common/Checkbox";
import Loader from "@/components/common/Loader";
import { Check } from "lucide-react";

export interface BaseColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  visible: boolean;
  hideOnMobile?: boolean;
  render?: (item: any, index: number) => React.ReactNode;
  className?: string;
  editable?: boolean;
  minWidth?: string;
}

export interface BaseReportTableProps<T> {
  // Data
  data: T[];
  columns: BaseColumn[];

  // Loading states
  loading?: boolean;

  // Selection
  selectable?: boolean;
  selectedItems?: string[];
  onToggleSelection?: (id: string) => void;
  onToggleAllSelection?: (checked: boolean) => void;
  getItemId?: (item: T) => string;

  // Sorting
  sortable?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (columnKey: string) => void;

  // Row interactions
  onRowClick?: (item: T) => void;

  // Table styling
  className?: string;
  tableClassName?: string;
  containerClassName?: string;

  // Empty state
  emptyMessage?: string;

  // Footer
  footer?: React.ReactNode;
}

export const BaseReportTable = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  selectable = false,
  selectedItems = [],
  onToggleSelection,
  onToggleAllSelection,
  getItemId = (item) => item._id || item.id || "new-row",
  sortable = false,
  sortBy,
  sortDirection,
  onSort,
  onRowClick,
  className = "",
  tableClassName = "",
  containerClassName = "",
  emptyMessage = "No data found",
  footer,
}: BaseReportTableProps<T>) => {
  
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );

  const isAllSelected = useMemo(() => {
    if (!selectable || data.length === 0) return false;
    return selectedItems.length === data.length && data.length > 0;
  }, [selectable, data, selectedItems]);

  const isIndeterminate = useMemo(() => {
    if (!selectable || data.length === 0) return false;
    return selectedItems.length > 0 && selectedItems.length < data.length;
  }, [selectable, data, selectedItems]);

  const handleSort = (columnKey: string) => {
    if (!sortable || !onSort) return;
    onSort(columnKey);
  };

  const handleRowClick = (e: React.MouseEvent, item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  const renderCell = (item: T, column: BaseColumn, rowIndex: number) => {
    if (column.render) {
      return column.render(item, rowIndex);
    }

    const value = item[column.key];
    let cellContent: React.ReactNode;

    if (typeof value === "object" && value !== null) {
      cellContent = value.name || value.label || JSON.stringify(value);
    } else {
      cellContent = value || "-";
    }

    return (
      <div className={clsx(
        "px-2 py-1",
        column.className
      )}>
        {cellContent}
      </div>
    );
  };

  return (
    <div className={clsx(
      "bg-white h-full flex flex-col overflow-hidden",
      className
    )}>
      {/* Scrollable container */}
      <div className={clsx(
        "flex-1 overflow-auto min-h-0",
        containerClassName
      )}>
        <table
          className={clsx(
            "w-full border-collapse",
            "whitespace-nowrap",
            // "[&_td]:p-0 [&_th]:p-0",
             "[&_td]:py-1 [&_th]:py-2", // Add this for vertical padding
  "[&_td]:px-2 [&_th]:px-2", 
            "[&_td]:border [&_td]:border-gray-300",
            "[&_th]:border [&_th]:border-gray-400",
            "[&_th]:bg-gray-100 [&_th]:font-medium [&_th]:text-xs",
            "[&_th:first-child]:text-left [&_th:not(:first-child)]:text-right",
            "[&_td:first-child]:text-left [&_td:not(:first-child)]:text-right",
            tableClassName
          )}
          style={{ tableLayout: "fixed" }}
        >
          <thead className="sticky top-0 z-10 bg-white px-3">
            <tr className='px-3'>
              {selectable && (
                <th 
                  className="sticky left-0 z-20 bg-gray-100 w-[40px] border border-gray-300"
                  style={{ width: '40px' }}
                >
                  <div className="flex items-center justify-center h-full px-1">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => onToggleAllSelection?.(e.target.checked)}
                    />
                  </div>
                </th>
              )}
              {visibleColumns.map((col, colIndex) => (
                <th
                  key={col.key}
                  className={clsx(
                    "border border-gray-300 text-sm font-medium bg-gray-100",
                    "whitespace-nowrap overflow-hidden",
                    col.sortable && sortable && "cursor-pointer hover:bg-gray-200",
                    col.hideOnMobile && "hidden sm:table-cell",
                    col.className
                  )}
                  style={{ 
                    minWidth: col.minWidth || '100px',
                    width: col.width
                  }}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className={clsx(
                    "flex items-center h-full px-2 py-2",
                    colIndex === 0 ? "justify-start" : "justify-end"
                  )}>
                    <span className="truncate text-xs">{col.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody >
            {loading ? (
              <tr className='px-3'>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                  className="text-center py-10"
                >
                  <Loader />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                  className="py-10"
                >
                  <div className="flex justify-center items-center h-[60vh] w-full text-center">
                    <div className="flex flex-col justify-center items-center text-center space-y-2">
                      <Check size={24} />
                      <span>{emptyMessage}</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, rowIndex) => {
                const itemId = getItemId(item);
                const isSelected = selectedItems.includes(itemId);

                return (
                  <tr
                    key={itemId}
                    className={clsx(
                      "border-b border-gray-100 hover:bg-gray-50",
                      isSelected && "bg-blue-50"
                    )}
                    onClick={(e) => handleRowClick(e, item)}
                  >
                    {selectable && (
                      <td 
                        className="border border-gray-300 p-0 bg-white sticky left-0 z-10 px-12"
                        style={{ width: '40px' }}
                      >
                        <div className="flex items-center justify-center p-1">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              onToggleSelection?.(itemId);
                            }}
                          />
                        </div>
                      </td>
                    )}
                    {visibleColumns.map((col, colIndex) => (
                      <td
                        key={col.key}
                        className={clsx(
                          "border border-gray-300",
                          "whitespace-nowrap overflow-hidden",
                          col.hideOnMobile && "hidden sm:table-cell",
                          col.className
                        )}
                        style={{ 
                          minWidth: col.minWidth || '100px',
                          height: '40px'
                        }}
                      >
                        {renderCell(item, col, rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {footer && <div className="border-t border-gray-200">{footer}</div>}
    </div>
  );
};

// Footer component for reports
interface ReportFooterProps {
  columnConfig: {
    key: string;
    width: string;
    minWidth: string;
    label: string;
  }[];
  totals: {
    count: number;
    totalTasks?: number;
    totalSubTasks?: number;
    totalEstimatedTime?: number;
    totalActualTime?: number;
  };
}

export const ReportFooter: React.FC<ReportFooterProps> = ({ 
  columnConfig, 
  totals 
}) => {
  return (
    <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
      <tbody>
        <tr className="bg-[#F5F5F5]">
          {columnConfig.map((col, index) => (
            <td 
              key={col.key}
              className="align-middle"
              style={{ 
                width: col.width,
                minWidth: col.minWidth,
                height: '32px'
              }}
            >
              <div className={clsx(
                "flex items-center h-full",
                index === 0 ? "justify-between" : "justify-end"
              )}>
                {index === 0 ? (
                  <>
                    <span className="text-sm">COUNT:</span>
                    <span className="text-sm font-medium">{totals.count}</span>
                  </>
                ) : (
                  <span className="text-sm font-medium">
                    {totals[col.key as keyof typeof totals] || 0}
                  </span>
                )}
              </div>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};